// Verificación del sello impreso en el documento de cotización. Ruta PÚBLICA:
// no depende de la sesión, y el token del QR es la única credencial. Ver
// `api/app/Http/Controllers/Api/VerificacionPublicaController.php`.

import { apiFetch, descargarArchivo } from "@/lib/api";

/**
 * En qué situación está el documento que alguien tiene en la mano.
 *
 * Espejo de `EstadoDeVerificacion` en el API. Es un vocabulario CERRADO y
 * distinto del estado interno de la cotización a propósito: acá no se nombra
 * el proceso de Consultek —revisores, borradores, versiones anuladas por
 * quién— sino lo que se le puede decir a alguien de afuera.
 */
export type EstadoDeVerificacion =
  | "no_emitida"
  | "vigente"
  | "vencida"
  | "aceptada"
  | "anulada";

/**
 * Lo que la pantalla de verificación muestra del documento.
 *
 * Son EXACTAMENTE los campos que el papel imprime, y esa correspondencia es
 * el punto: la pantalla existe para contrastar, no para informar. Un dato que
 * el documento no muestra no se podría comparar contra nada — solo se estaría
 * publicando detrás de un token.
 *
 * SIN LA VERSIÓN: el documento dejó de imprimirla, y además contaba las
 * recotizaciones, que es proceso interno. Quien escanea el sello no sabe que
 * hubo otras — un "versión 3" le haría suponer dos presupuestos que nunca
 * recibió. Que este papel esté reemplazado lo dice `estado`.
 */
export interface DocumentoVerificado {
  estado: EstadoDeVerificacion;
  /** "22192AA" — el mismo folio impreso junto al QR. */
  folio: string;
  cliente: string | null;
  propiedad: string | null;
  /** Ya formateado con su unidad ("UF 76,00"). La unidad es UF, nunca pesos. */
  total_uf: string;
  /** Con hora ("30-08-2026 14:12"), en hora de Chile. */
  emitida_en: string | null;
  /** Quien la CREÓ. `null` si esa persona se dio de baja. */
  emitida_por: string | null;
  /** `null` cuando nunca salió al cliente: ahí no hay vigencia que contar. */
  vigencia_hasta: string | null;
  /** sha256 de los bytes exactos que recibió el cliente. */
  documento_hash: string | null;
  /**
   * ¿Se puede bajar el PDF de este sello?
   *
   * NO SE DEDUCE DEL ESTADO, y por eso viaja como campo propio: `anulada` es
   * a la vez el caso que más sentido tiene descargar —el papel auténtico que
   * fue reemplazado— y el estado en que queda una cotización recotizada
   * mientras estaba en revisión, que nunca salió al cliente. El API decide
   * con lo que la pantalla no ve.
   */
  documento_descargable: boolean;
}

export async function fetchDocumentoVerificado(token: string): Promise<DocumentoVerificado> {
  const respuesta = await apiFetch<{ data: DocumentoVerificado }>(
    `/api/verificacion/${encodeURIComponent(token)}`,
  );
  return respuesta.data;
}

/**
 * Baja el PDF exacto que Consultek envió con ese sello.
 *
 * ES LO QUE VUELVE ÚTIL A LA HUELLA que la pantalla ya muestra: un sha256 no
 * se contrasta contra nada si no se tiene el archivo, y quien escanea suele
 * tener el papel —impreso, reenviado, dentro de un expediente— y no el
 * original.
 *
 * Va por `descargarArchivo` y no por un `<a href download>` por lo mismo que
 * la descarga interna: ese atributo se IGNORA entre orígenes distintos, y el
 * webapp vive en `app.*` mientras el API vive en `api.*`. El nombre lo pone el
 * servidor; el de acá es solo el respaldo por si esa cabecera no llega.
 */
export async function descargarDocumentoVerificado(token: string): Promise<void> {
  await descargarArchivo(
    `/api/verificacion/${encodeURIComponent(token)}/documento`,
    "cotizacion.pdf",
  );
}
